import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isMobile, isTablet } from 'react-device-detect';
import { useKeycloak } from '@react-keycloak/web';
import { FaFolderOpen, FaBars, FaDownload, IoMdDesktop, FaSignOutAlt, FaCheck, FaExternalLinkAlt } from '../../../../util/reactIcons';
// import { isMobile } from 'react-device-detect';
import { ToolbarButton, ButtonWithNoState } from '../../Common/styled/Button';
import { DropdownButton } from '../../Common/DropdownButton/DropdownButton';
// eslint-disable-next-line import/no-cycle,import/no-named-as-default
import AcknowledgeDialog from '../../Common/AcknowledgeDialog';
import store from '../../../../store/store';
import { setShowExplorer } from '../../../../actions/LayoutActions';
import { DownloadDialog } from '../../Common/DownloadDialog';
import toolNames from './toolNames';
import { getWorkstationURL, openWorkstationUrl } from '../../../../util/workstationUtils';
import { LoginData } from '../../../../util/LoginData';
import { isMobileDevice } from '../../../../util/deviceDetection';
import { closeChildViewports, openChildViewport } from '../../../../util/openChildViewport';
import { ButtonEnabled } from '../../../../util/ToolbarUtils';

const iconSize = '1.4em';

export const IndexLobbyButton = ({ handlerReset }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { config } = store.getState();

  if (!config.WITH_LOBBY) return null;
  if (!ButtonEnabled('INDEX')) return null;

  return (
    <ButtonWithNoState
      onClick={() => {
        closeChildViewports();
        history.push('/studies');
        if (handlerReset) handlerReset();
      }}
    >
      <FaFolderOpen size={iconSize} />
      <span>{t('toolbar.studies')}</span>
    </ButtonWithNoState>
  );
};

export const SeriesExplorerButton = ({ layout }) => {
  const { t } = useTranslation();
  const setHideExplorer = () => {
    const { showExplorer } = layout;
    if (showExplorer) {
      store.dispatch(setShowExplorer(false));
      return;
    }
    store.dispatch(setShowExplorer(true));
  };

  return (
    <ButtonWithNoState onClick={() => setHideExplorer()} className={layout.showExplorer ? 'selected' : ''}>
      <FaBars size={iconSize} />
      <span>{t('toolbar.explorer')}</span>
    </ButtonWithNoState>
  );
};

export const ExpandButton = ({ layout, display }) => {
  const { t } = useTranslation();

  if (isMobileDevice() || !display) return null;
  if (!ButtonEnabled('EXPAND')) return null;

  return (
    <ButtonWithNoState onClick={() => openChildViewport(layout, store.getState())}>
      <FaExternalLinkAlt size={iconSize} />
      <span>{t('toolbar.expand')}</span>
    </ButtonWithNoState>
  );
};

export const WorkstationButton = () => {
  const { t } = useTranslation();
  const [keycloak] = useKeycloak();
  const { layout, config } = store.getState();

  const isWindowsOS = navigator.userAgent.indexOf('Win') !== -1;
  const workstationUrl = getWorkstationURL(keycloak, layout, config);
  const wsEnabled = config.SHOW_WORKSTATION_BUTTON || ButtonEnabled('WORKSTATION');

  if (!isTablet && !isMobile && isWindowsOS && workstationUrl && wsEnabled) {
    return (
      <ButtonWithNoState onClick={() => openWorkstationUrl(workstationUrl)}>
        <IoMdDesktop size={iconSize} />
        <span>{t('toolbar.workstation')}</span>
      </ButtonWithNoState>
    );
  }
  return null;
};

export const DownloadButton = () => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const { layout, config } = store.getState();

  if (isMobileDevice() || !ButtonEnabled('DOWNLOAD')) return null;

  return (
    <>
      <ButtonWithNoState onClick={() => setShowDialog(true)}>
        <FaDownload size={iconSize} />
        <span>{t('download')}</span>
      </ButtonWithNoState>
      <DownloadDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        studyInstanceUID={layout.elements[0]?.StudyInstanceUID}
        config={config}
      />
    </>
  );
};

export const LogoutButton = () => {
  const { t } = useTranslation();
  const { authenticated, logout } = LoginData();

  if (!authenticated) return null;

  return (
    <ButtonWithNoState onClick={logout}>
      <FaSignOutAlt size={iconSize} />
      <span>{t('logout')}</span>
    </ButtonWithNoState>
  );
};

export const AcknowledgeButton = ({ config }) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const { username } = LoginData();
  const acknowledgeInterface = config.ACKNOWLEDGE_INTERFACE;
  const hasAcknowledgeInterface = acknowledgeInterface
    && typeof acknowledgeInterface === 'object'
    && !Array.isArray(acknowledgeInterface)
    && acknowledgeInterface.endpoint;

  if (isMobile || !hasAcknowledgeInterface) return null;

  return (
    <>
      <ButtonWithNoState
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setShowDialog(true)}
      >
        <FaCheck size={iconSize} />
        <span>{t('informed')}</span>
      </ButtonWithNoState>
      <AcknowledgeDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        username={username}
      />
    </>
  );
};

export const CommonButtons = ({ config, layout, handlerReset }) => {
  const { t } = useTranslation();
  const [canExpandToScreen, setCanExpandToScreen] = useState(() => (
    window.screen?.isExtended === true || typeof window.getScreenDetails === 'function'
  ));

  useEffect(() => {
    const updateScreenAvailability = () => {
      setCanExpandToScreen(
        window.screen?.isExtended === true || typeof window.getScreenDetails === 'function'
      );
    };

    window.addEventListener('focus', updateScreenAvailability);
    window.addEventListener('resize', updateScreenAvailability);
    window.screen?.addEventListener?.('change', updateScreenAvailability);
    document.addEventListener('visibilitychange', updateScreenAvailability);

    return () => {
      window.removeEventListener('focus', updateScreenAvailability);
      window.removeEventListener('resize', updateScreenAvailability);
      window.screen?.removeEventListener?.('change', updateScreenAvailability);
      document.removeEventListener('visibilitychange', updateScreenAvailability);
    };
  }, []);

  return (
    <>
      <IndexLobbyButton handlerReset={handlerReset} />
      <SeriesExplorerButton layout={layout} />
      <DownloadButton />
      <ExpandButton layout={layout} display={canExpandToScreen} />
      <WorkstationButton />
    </>
  );
};

export const CreateButton = ({ tool, currentTool, handlerSelectTool, isEnabledTool }) => {
  const { t } = useTranslation();
  const toolElementRef = useRef(null);
  tool.elementRef = toolElementRef;
  const hasDropdown = tool.name === toolNames.GROUPED_TOOLS
    || tool.name === toolNames.ROTATE_MENU;
  const isSelected = tool.name === currentTool;
  const isEnabled = tool.name === isEnabledTool
    || (tool.name === toolNames.ROTATE_MENU && currentTool === toolNames.ROTATE);
  return (
    <ToolbarButton
      onClick={() => handlerSelectTool(tool)}
      data-toolbar-group-trigger={hasDropdown ? 'true' : undefined}
      className={`${isSelected ? 'selected' : isEnabled ? 'enabled' : ''} ${tool.name === toolNames.HISTORY ? 'common' : ''}`}
      key={tool.name}
      ref={toolElementRef}
    >
      <tool.icon size={iconSize} />
      <span>
        {t(tool.label)} {hasDropdown ? <span className="toolArrow">&#9660;</span> : ''}
      </span>
    </ToolbarButton>
  );
};

export const CreateToggleButton = ({ tool, currentTool, selectedElement, handlerSelectTool }) => {
  const { t } = useTranslation();

  const toolName = t(tool.label);
  const copiedFeedback = 'Copied!';
  const [toolText, setToolText] = useState(toolName);
  copiedFeedback && setTimeout(() => setToolText(toolName), 2000);

  return (
    <ToolbarButton
      onClick={() => {
        handlerSelectTool(tool);
        {
          toolText !== copiedFeedback && setToolText(copiedFeedback);
        }
      }}
      className={tool.name === currentTool ? 'selected' : ''}
      key={tool.name}
    >
      {tool.name === 'Cine' && selectedElement.isPlayingCine ? (
        <>
          <tool.iconToggled size={iconSize} />
          <span>{t(tool.labelToggled)}</span>
        </>
      ) : tool.selfReset ? (
        <>
          <tool.icon size={iconSize} />
          <span>{toolText}</span>
        </>
      ) : (
        <>
          <tool.icon size={iconSize} />
          <span>{toolName}</span>
        </>
      )}
    </ToolbarButton>
  );
};

export const CreateOptionsButton = ({ tool, currentTool, handlerSelectTool, onDropdownMenuToggle }) => {
  const { label, icon, name } = tool;
  const resolvedOptions = typeof tool.options === 'function' ? tool.options() : tool.options;
  const isDisabled = typeof tool.disabled === 'function' ? tool.disabled() : tool.disabled;
  const isSelected = name === currentTool
    || (tool.activeToolName && tool.activeToolName === currentTool);
  return (
    <DropdownButton
      tool={tool}
      selectHandler={handlerSelectTool}
      className={`${isSelected ? 'selected' : ''} ${name === toolNames.LANGUAGE ? 'common' : ''} ${isDisabled ? 'disabled' : ''}`}
      label={label}
      icon={icon}
      options={resolvedOptions || []}
      disabled={isDisabled}
      isPersistent={false}
      isTool={tool.isTool || false}
      menuClassName={tool.dropdownMenuClassName}
      key={label}
      onDropdownMenuToggle={onDropdownMenuToggle}
    />
  );
};
